import { useState, useEffect } from 'react';
import axios from 'axios';

const WEATHER_API_BASE_URL = 'https://api.weather.gov';

export const useLatestObservationData = (
  forecastOffice,
  refreshRate,
  x,
  y,
  counter,
  setCounter,
) => {
  const [data, setData] = useState([]);
  const runImmediately = counter === 0;
  const refreshTimer = runImmediately ? 0 : refreshRate;

  useEffect(() => {
    const fetchData = async () => {
      setCounter(counter++);
      const forecastData =
        (await fetchForecastData(forecastOffice, x, y)) ?? [];
      setData(forecastData.slice(0, 9));
    };

    const interval = setInterval(() => {
      fetchData().catch(console.error);
    }, refreshTimer);

    return () => clearInterval(interval);
  }, [forecastOffice, refreshRate, x, y, counter, setCounter, refreshTimer]);

  return data;
};

export const fetchForecastData = async (forecastOffice, x, y) => {
  try {
    const { data } = await axios.get(
      `${WEATHER_API_BASE_URL}/gridpoints/${forecastOffice}/${x},${y}/forecast`,
      {
        headers: {
          'content-type': 'application/geo+json',
        },
      },
    );
    return (await data?.properties?.periods) ?? [];
  } catch (error) {
    console.log('Error getting updated forecast', error);
  }
};

export const fetchLatestObservationData = async (requireQC, stationId) => {
  try {
    const { data } = await axios.get(
      `${WEATHER_API_BASE_URL}/stations/${stationId}/observations/latest?require_qc=${requireQC}`,
      {
        headers: {
          'content-type': 'application/geo+json',
        },
      },
    );
    return (await data?.properties) ?? [];
  } catch (error) {
    console.log('Error getting updated forecast', error);
  }
};

export const fetchObservationData = async (end, start, stationId) => {
  try {
    const { data } = await axios.get(
      `${WEATHER_API_BASE_URL}/stations/${stationId}/observations?limit=5`,
      {
        headers: {
          'content-type': 'application/geo+json',
        },
      },
    );
    return (await data?.features) ?? [];
  } catch (error) {
    console.log('Error getting updated forecast', error);
  }
};

export async function getLANLWeatherTowersQuery(
  includeStatus = ['A', 'I'],
  showAStatusLabel = true,
  excludedTowers = [],
) {
  const date = Date();
  const today = new Date(date).toLocaleDateString('en-ZA');
  let stationList = [];

  try {
    const response = await axios.get(
      'https://d2lg1boo4xsaar.cloudfront.net/api-cache/wm-api-tower.json',
    );

    getWeatherAPIRows(response).forEach((station, index) => {
      //skip first row because it's a header
      if (index === 0) {
        return;
      }
      const stationName = station.Data[0].VarCharValue.split('_')[0];
      const stationType = station.Data[1].VarCharValue;
      const stationInterval = station.Data[2].VarCharValue.slice(0, 2);
      const stationStatus = station.Data[3].VarCharValue;
      const stationView = station.Data[4].VarCharValue;
      const minDate = station.Data[5].VarCharValue;
      const maxDate = station.Data[6].VarCharValue;

      // check if station is already in the list
      const stationIndex = stationList.findIndex(
        (station) => station.value === stationName,
      );
      if (stationIndex === -1) {
        // if not, add it
        stationList.push({
          label: `${stationType?.replace(/^(TA)/, '$1-')}${
            showAStatusLabel
              ? stationStatus === 'A'
                ? ' (active)'
                : ' (inactive)'
              : ''
          }`,
          value: stationName,
          options: [stationInterval],
          defaultInterval: stationInterval,
          views: [stationView],
          minDate: minDate,
          maxDate: maxDate ?? today,
          stationStatus: stationStatus,
        });
      } else {
        // if it is, add the interval to the options and add the view to the views
        stationList[stationIndex].options.push(stationInterval);
        stationList[stationIndex].views.push(stationView);
      }
    });
    // if the defaultInterval is 24, add '[24 hour only] to the end of the label
    stationList.forEach((station) => {
      const viewCount = station.views ?? [];
      if (viewCount.length < 2) {
        station.label = `${station.label} [${station.defaultInterval} hour only]`;
      }
    });
    return (
      stationList
        ?.filter(({ stationStatus }) => includeStatus.includes(stationStatus))
        ?.filter(({ value }) => !excludedTowers.includes(value)) ?? []
    );
  } catch (e) {
    console.log('token error', e);
  }
}

export async function getLANLWeatherDataQuery(
  columns,
  endDate,
  output,
  startDate,
  setProcessing,
  view,
  setError,
) {
  try {
    return await axios
      .post(
        'https://z2aippcgesuwnxlfg4yg53pleq0xorfj.lambda-url.us-west-1.on.aws',
        {
          data: {
            columns: columns,
            where: `tower_date between '${startDate}' and '${endDate}' order by datetime`,
            output: output,
            table: view,
          },
        },
      )
      .then((response) => {
        const data = response.data;
        if (output === 'url' && data) {
          const downloadUrl = data?.download_url ?? null;
          if (downloadUrl) {
            window.location.assign(downloadUrl);
          } else {
            setError(
              'There was an error processing your download, please try again. ',
            );
          }
          setProcessing(false);
        } else {
          return data;
        }
      })
      .catch((error) => {
        console.log(error);
        setProcessing(false);
      });
  } catch (e) {
    console.log('token error', e);
  }
}

export async function getLANLWeatherVariablesQuery(setLoading) {
  try {
    const response = await axios.get(
      'https://d2lg1boo4xsaar.cloudfront.net/api-cache/wm-api-tower-columns.json',
    );
    const data = getWeatherAPIRows(response);
    setLoading(false);
    return (
      data
        ?.slice(1)
        ?.map(({ Data }) => ({
          tower: Data[1].VarCharValue,
          variable_name: Data[2].VarCharValue,
          variable_description: Data[4].VarCharValue,
          variable_unit: Data[3].VarCharValue,
        }))
        .sort((a, b) => a.variable_name.localeCompare(b.variable_name))
        .filter(
          ({ variable_name }) => !['DOY', 'DATETIME'].includes(variable_name),
        ) ?? []
    );
  } catch (e) {
    console.log('token error', e);
  }
}

export async function getLatestDataQuery(view) {
  try {
    const response = await axios.get(
      `https://d2lg1boo4xsaar.cloudfront.net/api-cache/wm-api-latest-tower-data-${view}.json`,
    );

    const headerRow = getWeatherAPIRows(response)[0]?.Data;
    return getWeatherAPIRows(response)
      .map((data) => {
        return Object.assign(
          {},
          ...data.Data.map(({ VarCharValue }, dataIndex) => ({
            [headerRow[dataIndex].VarCharValue]: VarCharValue,
          })),
        );
      })
      .pop();
  } catch (e) {
    console.log('token error', e);
  }
}

export async function getLatestTA6DataQuery() {
  try {
    const response = await axios.get(
      'https://d2lg1boo4xsaar.cloudfront.net/api-cache/wm-api-latest-tower-data-ta6-15.json',
    );

    const headerRow = getWeatherAPIRows(response)[0]?.Data;
    return getWeatherAPIRows(response)
      .map((data) => {
        return Object.assign(
          {},
          ...data.Data.map(({ VarCharValue }, dataIndex) => ({
            [headerRow[dataIndex].VarCharValue]: VarCharValue,
          })),
        );
      })
      .pop();
  } catch (e) {
    console.log('token error', e);
  }
}

function getWeatherAPIRows(response) {
  return response?.data?.ResultSet?.Rows ?? [];
}
